import { useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { EntryFormDialog } from '@/components/common/EntryFormDialog';
import { useUpdateJob } from '../hooks/useUpdateJob';
import type { JobDetailDto } from '../types';

interface JobEditFormProps {
  job: JobDetailDto;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

export function JobEditForm({ job, isOpen, onClose, onSave }: JobEditFormProps) {
  // Track previous state to detect when to reset form (React-recommended pattern)
  const [prevIsOpen, setPrevIsOpen] = useState(false);
  const [prevJobId, setPrevJobId] = useState(job.id);
  const [description, setDescription] = useState(job.description || '');
  const [notes, setNotes] = useState(job.notes || '');
  const { updateJobDetails, isUpdating, error } = useUpdateJob();

  // Reset form when dialog opens or job changes (during render, not in effect)
  if ((isOpen && !prevIsOpen) || job.id !== prevJobId) {
    setPrevIsOpen(isOpen);
    setPrevJobId(job.id);
    setDescription(job.description || '');
    setNotes(job.notes || '');
  } else if (!isOpen && prevIsOpen) {
    setPrevIsOpen(false);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const result = await updateJobDetails(job.id, {
      description: description || undefined,
      notes: notes || undefined,
    });

    if (result) {
      onSave();
      onClose();
    }
  };

  const handleClose = () => {
    // Reset form to original values
    setDescription(job.description || '');
    setNotes(job.notes || '');
    onClose();
  };

  return (
    <EntryFormDialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Job Details"
      error={error}
      isSubmitting={isUpdating}
      submitLabel="Save Changes"
      editLabel="Save Changes"
      isEditing={true}
      onSubmit={handleSubmit}
      maxWidth="sm:max-w-[600px]"
    >
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter job description..."
          rows={4}
          className="text-sm"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Enter job notes..."
          rows={4}
          className="text-sm"
        />
      </div>
    </EntryFormDialog>
  );
}
